// Run-clip pacing: the clip plays at the rate that keeps a planted foot still on the ground, so a hero's
// feet don't skate. How fast a clip's planted foot travels backwards (its stride speed) is read off the
// clip itself once per clip, and the hero's real ground speed is measured from how far the rig actually
// went — slows, haste, a ford and network interpolation all included, core untouched.
import * as THREE from 'three';

const SAMPLES = 120;
/** A foot counts as planted in the lowest fifth of its lift. */
const CONTACT = 0.2;
/** Smoothing of the measured ground speed, s: evens out 60 Hz sim steps under a faster render loop. */
const PACE_TAU = 0.12;
/** Faster than any walk: a blink, a swap or a resync snap, not a stride. */
const TELEPORT = 20;
const MIN_RATE = 0.3, MAX_RATE = 3.5;

const strideSpeeds = new WeakMap<THREE.AnimationClip, number | null>();

/** Backward speed of the clip's planted feet in `model`'s own (unscaled) space, m/s; null without foot
    bones. Poses `model` through the clip with a throwaway mixer — call before its own mixer plays. */
export function strideSpeed(model: THREE.Object3D, clip: THREE.AnimationClip): number | null {
  if (strideSpeeds.has(clip)) return strideSpeeds.get(clip)!;
  const feet: THREE.Object3D[] = [];
  model.traverse(o => { if (/foot/i.test(o.name)) feet.push(o); });
  let speed: number | null = null;
  if (feet.length && clip.duration > 0) {
    const mixer = new THREE.AnimationMixer(model);
    mixer.clipAction(clip).play();
    const dt = clip.duration / SAMPLES, p = new THREE.Vector3();
    const tracks = feet.map(() => [] as THREE.Vector3[]);
    for (let i = 0; i <= SAMPLES; i++) {
      mixer.setTime(i * dt);
      model.updateMatrixWorld(true);
      feet.forEach((f, k) => tracks[k]!.push(model.worldToLocal(f.getWorldPosition(p)).clone()));
    }
    mixer.stopAllAction();
    mixer.uncacheRoot(model);
    let dist = 0, time = 0;
    for (const tr of tracks) {
      const [d, t] = plantedTravel(tr, dt);
      dist += d; time += t;
    }
    if (dist > 0 && time > 0) speed = dist / time;
  }
  strideSpeeds.set(clip, speed);
  return speed;
}

/* One foot's backward travel while planted, and the time it took. Forward is the way the foot swings
   while lifted, whichever horizontal axis the rig faces along. */
function plantedTravel(tr: THREE.Vector3[], dt: number): [number, number] {
  const ys = tr.map(v => v.y), lo = Math.min(...ys), hi = Math.max(...ys);
  if (hi - lo < 1e-3) return [0, 0];
  const planted = lo + (hi - lo) * CONTACT, lifted = hi - (hi - lo) * CONTACT;
  const fwd = new THREE.Vector3();
  for (let i = 1; i < tr.length; i++) if (tr[i]!.y > lifted) fwd.add(tr[i]!.clone().sub(tr[i - 1]!));
  fwd.y = 0;
  if (fwd.lengthSq() < 1e-12) return [0, 0];
  fwd.normalize();
  let dist = 0, time = 0;
  for (let i = 1; i < tr.length; i++) {
    if (tr[i]!.y > planted || tr[i - 1]!.y > planted) continue;
    const back = -tr[i]!.clone().sub(tr[i - 1]!).dot(fwd);
    if (back > 0) { dist += back; time += dt; }
  }
  return [dist, time];
}

export type Pace = { x: number; z: number; speed: number };

/** Updates the smoothed ground speed from where the hero stands now; `dt` is the sim time since the last
    call. Only a walking hero is measured: a hero setting off again starts from the pace it last had. */
export function trackPace(pace: Pace, pos: { x: number; z: number }, moving: boolean, dt: number): number {
  if (moving && dt > 0) {
    const v = Math.hypot(pos.x - pace.x, pos.z - pace.z) / dt;
    if (v < TELEPORT) pace.speed += (v - pace.speed) * (1 - Math.exp(-dt / PACE_TAU));
  }
  pace.x = pos.x; pace.z = pos.z;
  return pace.speed;
}

/** Playback rate of a run clip whose feet travel at `stride` m/s, for a hero going `speed` m/s. */
export function runRate(speed: number, stride: number): number {
  return THREE.MathUtils.clamp(speed / stride, MIN_RATE, MAX_RATE);
}
